import { getMessageAttribute, hasMessageAttribute } from 'modules/message/utils'
import { any, last, pathOr, uniq, without } from 'ramda'
import { Message, ViewerRecipientStatus } from 'types/message'
import { createReducer } from 'utils/redux'

const INITIAL_STATE = {
  hasSentImpression: false,
  initialConversationId: null,
  active: [],
  passive: []
}

const receiveNewBotCampaignMessageHandler = ({ action, draft }) => {
  const message: Partial<Message> = pathOr({}, ['data'], action.payload)
  const playbookId = getMessageAttribute(message, 'playbookId')

  draft.active = uniq([...draft.active, playbookId])
}

const receiveInitialActiveConversationHandler = ({ action, draft }) => {
  const conversationId = pathOr(null, ['payload', 'conversation', 'conversation', 'id'], action)
  if (!!conversationId) {
    draft.initialConversationId = conversationId
  }
}

const receiveMessagesForConversationHandler = ({ action, draft }: { action: { payload: { messages: Message[] } }, draft: Record<string, any> }) => {
  const { payload: { messages } } = action
  const firstMessage = last(action.payload.messages) // This list is in reverse order, therefore the first message at the end

  const hasStartInteraction = hasMessageAttribute(firstMessage, 'startInteraction')

  // Sometimes the first message, one with startInteraction attribute, has no viewerRecipientStatus set,
  // so we check if any message is read, which means that this conversation has been seen before
  const areAnyMessagesRead = any(message => message.viewerRecipientStatus === ViewerRecipientStatus.READ, messages)

  // To mark the playbook as passive, we check that:
  // 1. This is the first conversation we get in the widget's lifetime
  // 2. The first message has a startInteraction attribute
  // 3. Any of the messages have been marked read
  if (
    hasStartInteraction &&
    areAnyMessagesRead &&
    firstMessage.conversationId === draft.initialConversationId
  ) {
    const playbookId = getMessageAttribute(firstMessage, 'playbookId')
    draft.passive = uniq([...draft.passive, playbookId])
  }
}

const removeTriggerableCampaignImpressionHandler = ({ action, draft }) => {
  const { source, playbookId } = action.payload
  if (source === 'active') {
    draft.active = without([playbookId], draft.active)
  } else if (source === 'passive') {
    draft.passive = without([playbookId], draft.passive)
  }
}

const setHasSentImpressionHandler = ({ draft }) => {
  draft.hasSentImpression = true
}

const actions = {
  SET_HAS_SENT_IMPRESSION: setHasSentImpressionHandler,
  RECEIVE_ACTIVE_CAMPAIGN: receiveNewBotCampaignMessageHandler,
  RECEIVE_INITIAL_ACTIVE_CONVERSATION: receiveInitialActiveConversationHandler,
  RECEIVE_MESSAGES_FOR_CONVERSATION: receiveMessagesForConversationHandler,
  REMOVE_TRIGGERABLE_CAMPAIGN_IMPRESSION: removeTriggerableCampaignImpressionHandler
}

export default createReducer(
  INITIAL_STATE,
  actions
)
